import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { X, Stethoscope, BookOpen, ArrowRight, Maximize2 } from 'lucide-react'
import { HelpArticleContent } from '@/components/help/HelpArticleContent'
import { HelpFeedback } from '@/components/help/HelpFeedback'
import { helpArticlePath, useHelpArticle } from '@/components/help/helpApi'
import { useHelpPanelStore } from '@/stores/helpPanel'

interface HelpPanelProps {
  slug: string
  onClose: () => void
}

/**
 * The help quick view (CLA-1838): an article in a side panel over the page
 * you are on. Closing it (the backdrop, ×, Esc) leaves you there. "Take me
 * there" and the body's `claro:` links close it and go; "Open in Help" goes
 * to the full article page, `/help/:slug`.
 */
export function HelpPanel({ slug, onClose }: HelpPanelProps) {
  const navigate = useNavigate()
  const { data: article, isLoading: loading } = useHelpArticle(slug)

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const go = (path: string) => {
    onClose()
    navigate(path)
  }
  const takeMeThere = article?.target?.web_path

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />

      {/* Panel */}
      <div
        role="dialog"
        aria-label={article?.title ?? 'Help'}
        className="relative w-full max-w-lg bg-surface shadow-xl flex flex-col h-full"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-border-subtle shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {article?.content_type === 'clinical_reference' ? (
              <Stethoscope className="w-4 h-4 text-claro-600 shrink-0" />
            ) : (
              <BookOpen className="w-4 h-4 text-claro-600 shrink-0" />
            )}
            <h2 className="text-sm font-semibold text-ink truncate">
              {loading ? 'Loading...' : article?.title || 'Article not found'}
            </h2>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => go(helpArticlePath(slug))}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-claro-700 hover:bg-claro-50"
            >
              <Maximize2 className="w-3 h-3" />
              Open in Help
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="p-1.5 rounded-lg hover:bg-surface-secondary transition-colors"
            >
              <X className="w-4 h-4 text-ink-faint" />
            </button>
          </div>
        </div>

        {/* Body — rendered from curated markdown, not user input */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="space-y-3">
              <div className="h-4 bg-surface-secondary rounded w-3/4 animate-pulse" />
              <div className="h-4 bg-surface-secondary rounded w-full animate-pulse" />
              <div className="h-4 bg-surface-secondary rounded w-5/6 animate-pulse" />
            </div>
          ) : article ? (
            <div className="space-y-6">
              {takeMeThere && (
                <button
                  type="button"
                  onClick={() => go(takeMeThere)}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-claro-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-claro-700"
                >
                  Take me there
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
              <HelpArticleContent article={article} onNavigate={go} />
            </div>
          ) : (
            <p className="text-sm text-ink-faint">Article not found.</p>
          )}
        </div>

        {/* Footer */}
        {article && (
          <div className="px-6 py-3 border-t border-border-subtle shrink-0">
            <HelpFeedback key={article.slug} article={article} />
          </div>
        )}
      </div>
    </div>
  )
}

/** Mounted once in the app shell: the quick view, when ⌘K has opened one. */
export function HelpQuickView() {
  const slug = useHelpPanelStore((s) => s.slug)
  const close = useHelpPanelStore((s) => s.close)
  if (!slug) return null
  return <HelpPanel slug={slug} onClose={close} />
}
